// Neutral professional placeholder for SIL/SDA listings that don't yet have
// a verified property photograph.
//
// Style rules (per operational feedback):
//   • Photographic-style tint — soft dusk gradient, not cartoon.
//   • No illustrated houses. No flat drawings. No AI concept art.
//   • Clear "Property photo coming soon" messaging.
//   • Show suburb + state so the placeholder is contextually honest.
//   • Small "Demo listing" corner label so viewers know this isn't a real
//     Alpha vacancy photograph.
//
// Real property photos should REPLACE this placeholder via the admin drag-
// and-drop uploader — POST /api/admin/properties/{slug}/photo.

// Palettes — muted photographic tones only. No saturated cartoon colors.
const PALETTES = [
  { id: "dusk-warm",  top: "#C8B6A0", mid: "#A0917C", bot: "#4E463B", accent: "#7C6749" },
  { id: "dusk-blue",  top: "#B7C3CE", mid: "#8A97A4", bot: "#3E4753", accent: "#4C5D6F" },
  { id: "sage",       top: "#B7BFAE", mid: "#8B9682", bot: "#414A3B", accent: "#5B6B4C" },
  { id: "stone",      top: "#C5BFB6", mid: "#948C82", bot: "#4A443D", accent: "#6E6558" },
  { id: "clay",       top: "#C6A798", mid: "#9B7F6F", bot: "#4B382E", accent: "#7C5A48" },
  { id: "misty",      top: "#B9BFC4", mid: "#8C939A", bot: "#41474C", accent: "#5B646C" },
];

function paletteFor(slug) {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  return PALETTES[h % PALETTES.length];
}

/**
 * Return an inline SVG data URI showing a NEUTRAL photographic-style
 * placeholder for a property that doesn't yet have a real photo.
 *
 * @param {string} slug   Property slug — drives palette variation.
 * @param {string} label  A short location string (e.g. "Broadmeadows, VIC").
 */
export function svgHomeDataUrl(slug, label = "Property photo coming soon") {
  const p = paletteFor(slug);
  // Soft photographic-style vertical gradient. No house artwork.
  // Camera-outline glyph at centre. Contextual suburb+state at bottom.
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600" role="img" aria-label="${label}">
  <defs>
    <linearGradient id="g-${p.id}" x1="0" x2="0" y1="0" y2="1">
      <stop offset="0" stop-color="${p.top}"/>
      <stop offset="0.55" stop-color="${p.mid}"/>
      <stop offset="1" stop-color="${p.bot}"/>
    </linearGradient>
    <radialGradient id="v-${p.id}" cx="0.5" cy="0.5" r="0.75">
      <stop offset="0" stop-color="#000" stop-opacity="0"/>
      <stop offset="1" stop-color="#000" stop-opacity="0.28"/>
    </radialGradient>
  </defs>
  <rect width="800" height="600" fill="url(#g-${p.id})"/>
  <rect width="800" height="600" fill="url(#v-${p.id})"/>
  <!-- subtle horizon line -->
  <rect y="440" width="800" height="2" fill="#000" opacity="0.08"/>
  <!-- subtle grain via dashed lines -->
  <g stroke="#000" stroke-width="1" opacity="0.06">
    <line x1="0" y1="120" x2="800" y2="120"/>
    <line x1="0" y1="200" x2="800" y2="200"/>
    <line x1="0" y1="280" x2="800" y2="280"/>
    <line x1="0" y1="500" x2="800" y2="500"/>
  </g>
  <!-- camera glyph -->
  <g transform="translate(400,290)" fill="none" stroke="#F5F1EA" stroke-width="4" opacity="0.85">
    <rect x="-56" y="-34" width="112" height="70" rx="8"/>
    <circle cx="0" cy="4" r="20"/>
    <circle cx="0" cy="4" r="9" fill="#F5F1EA" opacity="0.6"/>
    <rect x="-32" y="-46" width="24" height="12" rx="2"/>
  </g>
  <!-- copy -->
  <text x="400" y="380" text-anchor="middle" font-family="Georgia, serif" font-size="22" fill="#F5F1EA" opacity="0.95">Property photo coming soon</text>
  <text x="400" y="418" text-anchor="middle" font-family="system-ui, -apple-system, sans-serif" font-size="14" letter-spacing="2" fill="#F5F1EA" opacity="0.65">${label.toUpperCase()}</text>
  <!-- corner tag -->
  <g transform="translate(24,24)">
    <rect width="132" height="26" rx="13" fill="#000" opacity="0.55"/>
    <text x="66" y="17" text-anchor="middle" font-family="system-ui, -apple-system, sans-serif" font-size="11" letter-spacing="1.6" fill="#F5F1EA">DEMO LISTING</text>
  </g>
</svg>`.replace(/\s+/g, " ").trim();
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/**
 * Neutral photographic-style content tile — for service/blog/hero cards
 * where using stock imagery risks looking aged-care / clinical / luxury.
 *
 * @param {string} key    Content key (e.g. "sil", "community-nursing") — drives palette.
 * @param {string} label  Short eyebrow (e.g. "SIL · Supported Independent Living").
 */
export function svgContentTileUrl(key, label) {
  const p = paletteFor(key || "content");
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1600 900" role="img" aria-label="${label}">
  <defs>
    <linearGradient id="tg-${p.id}" x1="0" x2="1" y1="0" y2="1">
      <stop offset="0" stop-color="${p.top}"/>
      <stop offset="0.55" stop-color="${p.mid}"/>
      <stop offset="1" stop-color="${p.bot}"/>
    </linearGradient>
    <radialGradient id="tv-${p.id}" cx="0.5" cy="0.5" r="0.75">
      <stop offset="0" stop-color="#000" stop-opacity="0"/>
      <stop offset="1" stop-color="#000" stop-opacity="0.32"/>
    </radialGradient>
  </defs>
  <rect width="1600" height="900" fill="url(#tg-${p.id})"/>
  <rect width="1600" height="900" fill="url(#tv-${p.id})"/>
  <g stroke="#000" stroke-width="1" opacity="0.05">
    <line x1="0" y1="180" x2="1600" y2="180"/>
    <line x1="0" y1="360" x2="1600" y2="360"/>
    <line x1="0" y1="540" x2="1600" y2="540"/>
    <line x1="0" y1="720" x2="1600" y2="720"/>
  </g>
  <text x="800" y="470" text-anchor="middle" font-family="Georgia, serif" font-style="italic" font-size="46" fill="#F5F1EA" opacity="0.92">${label}</text>
  <text x="800" y="520" text-anchor="middle" font-family="system-ui, -apple-system, sans-serif" font-size="18" letter-spacing="4" fill="#F5F1EA" opacity="0.62">ALPHA COMMUNITY CARE · NDIS</text>
</svg>`.replace(/\s+/g, " ").trim();
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}
